import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import FeaturesGrid from '../components/sections/FeaturesGrid';
import FinalCTA from '../components/sections/FinalCTA';
import { useT } from '../i18n/useT';
import './FeaturesPage.css';

const CATEGORY_KEYS = ['scanning', 'organisation', 'search', 'export', 'security', 'productivity'];

const CATEGORY_ICONS = {
  scanning: (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <rect x="3" y="3" width="22" height="22" rx="3" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M9 14h10M14 9v10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  ),
  organisation: (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="22" height="18" rx="2" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M3 10h22M8 5V3M20 5V3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  ),
  search: (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <circle cx="13" cy="13" r="8" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M19 19l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  ),
  export: (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <path d="M9 17l5-5 5 5M14 12v10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5 7h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  ),
  security: (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <path d="M14 3l9 4v7c0 5-4 9-9 11C9 23 5 19 5 14V7l9-4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
      <path d="M10 14l3 3 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  productivity: (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <path d="M14 4v8l4 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      <circle cx="14" cy="15" r="9" stroke="currentColor" strokeWidth="1.6"/>
    </svg>
  ),
};

export default function FeaturesPage() {
  const t = useT();
  const p = t.pages.features;
  const ft = t.featuresTable;

  return (
    <>
      <Helmet>
        <title>{p.title} – Docudi</title>
        <meta name="description" content={p.sub} />
        <link rel="canonical" href="https://[YOUR-DOMAIN.com]/features" />
      </Helmet>

      {/* Page hero */}
      <section className="page-hero features-hero">
        <div className="container">
          <div className="page-hero__content text-center">
            <div className="badge">{p.title}</div>
            <h1 className="page-hero__title">
              {p.headline.split(',')[0]},<br />
              <span className="gradient-text">{p.headline.split(',')[1]?.trim()}</span>
            </h1>
            <p className="page-hero__sub">{p.sub}</p>
            <div className="page-hero__actions">
              <Link to="/contact" className="btn btn-primary btn-lg">
                {t.finalCta.ctaPrimary}
              </Link>
              <a href="#features-table" className="btn btn-secondary btn-lg">
                {ft.headline}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main features grid */}
      <FeaturesGrid limit={0} showCTA={false} />

      {/* Advanced feature table */}
      <section id="features-table" className="features-table section" aria-labelledby="features-table-heading">
        <div className="container">
          <div className="section-header">
            <h2 id="features-table-heading">{ft.headline}</h2>
            <p>{ft.sub}</p>
          </div>

          <div className="features-table__grid">
            {CATEGORY_KEYS.map((key) => {
              const cat = ft.categories[key];
              return (
                <div key={key} className="features-table__category">
                  <div className="features-table__cat-header">
                    <span className="features-table__cat-icon">{CATEGORY_ICONS[key]}</span>
                    <h3 className="features-table__cat-title">{cat.title}</h3>
                  </div>
                  <ul className="features-table__items">
                    {cat.items.map((item, i) => (
                      <li key={i} className="features-table__item">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                          <circle cx="8" cy="8" r="7" fill="var(--color-primary-100)"/>
                          <path d="M5 8l2 2 4-4" stroke="var(--color-primary)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
